/**
 * Collection View Switcher
 * Handles switching between grid, list, and table views
 */

class CollectionViewSwitcher {
  constructor() {
    this.productGrid = document.getElementById('product-grid');
    this.viewToggleButtons = document.querySelectorAll('.view-toggle');
    this.currentView = 'grid'; // default view
    this.tableHeader = null;
    
    this.init();
  }

  init() {
    if (!this.productGrid || !this.viewToggleButtons.length) {
      return;
    }

    // Add event listeners to view toggle buttons
    this.viewToggleButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const view = button.dataset.view;
        this.setView(view);
      });
    });

    // Load saved view preference from localStorage FIRST before setting default
    const savedView = localStorage.getItem('collection-view-preference');
    if (savedView && ['grid', 'table'].includes(savedView)) {
      // Don't trigger the transition on initial load
      this.currentView = savedView === 'table' ? 'grid' : savedView; // Set to opposite so setView will work
      this.setView(savedView);
    } else {
      // Set initial view to grid only if no saved preference
      this.setView('grid');
    }
  }

  setView(viewType) {
    if (this.currentView === viewType) return;
    if (!['grid', 'table'].includes(viewType)) return; // Only allow grid and table

    // Add switching animation
    this.productGrid.classList.add('switching-view');

    // Update after a short delay for smooth transition
    setTimeout(() => {
      // Remove all view classes
      this.productGrid.classList.remove('view-grid', 'view-table');

      // Add new view class only for table view
      // Grid view uses the original Shopify grid classes
      if (viewType === 'table') {
        this.productGrid.classList.add('view-table');
        this.createTableHeader();
        this.showSelectAllButton();
        this.enforceInStockFilter();
      } else {
        // Grid view - remove table header and let original grid work
        this.removeTableHeader();
        this.hideSelectAllButton();
        this.removeInStockFilter();
      }

      // Update button states
      this.updateButtonStates(viewType);

      // Update current view
      this.currentView = viewType;

      // Save preference to localStorage
      localStorage.setItem('collection-view-preference', viewType);

      // Remove switching animation
      this.productGrid.classList.remove('switching-view');

      // Trigger custom event for other scripts that might need to know about view changes
      document.dispatchEvent(new CustomEvent('collectionViewChanged', {
        detail: { view: viewType }
      }));

    }, 150);
  }

  updateButtonStates(activeView) {
    this.viewToggleButtons.forEach(button => {
      const isActive = button.dataset.view === activeView;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive.toString());
    });
  }

  createTableHeader() {
    // Remove existing table header if it exists
    this.removeTableHeader();

    // Create table header
    this.tableHeader = document.createElement('div');
    this.tableHeader.className = 'table-header';
    this.tableHeader.innerHTML = `
      <div class="table-checkbox-col">
        <input type="checkbox" id="select-all-products" class="table-select-all" aria-label="Select all products">
      </div>
      <div>Image</div>
      <div>Product</div>
      <div>Options</div>
      <div>Price</div>
      <div class="hide-mobile">Action</div>
    `;

    // Insert table header before the product grid
    this.productGrid.parentNode.insertBefore(this.tableHeader, this.productGrid);
  }

  removeTableHeader() {
    if (this.tableHeader) {
      this.tableHeader.remove();
      this.tableHeader = null;
    }
  }

  // Method to refresh the view (useful for dynamic content loading)
  refresh() {
    if (this.currentView === 'table') {
      this.createTableHeader();
    }
  }

  // Method to get current view
  getCurrentView() {
    return this.currentView;
  }

  // Method to check if a specific view is active
  isViewActive(viewType) {
    return this.currentView === viewType;
  }

  // Show/hide Select All button
  showSelectAllButton() {
    const selectAllContainer = document.querySelector('.select-all-container');
    if (selectAllContainer) {
      selectAllContainer.classList.add('show');
      selectAllContainer.style.display = 'block';
    }
  }

  hideSelectAllButton() {
    const selectAllContainer = document.querySelector('.select-all-container');
    if (selectAllContainer) {
      selectAllContainer.classList.remove('show');
      selectAllContainer.style.display = 'none';
    }
  }

  // Enforce in-stock filter for table view
  enforceInStockFilter() {
    // Find the availability/stock filter checkbox
    const availabilityFilter = document.querySelector('input[name="filter.v.availability"][value="1"]');

    if (availabilityFilter && !availabilityFilter.checked) {
      // Store original state before enforcing
      sessionStorage.setItem('pre-table-view-stock-filter', 'unchecked');

      // Check the in-stock filter
      availabilityFilter.checked = true;

      // Store table view preference before filter change
      localStorage.setItem('collection-view-preference', 'table');

      // Trigger the filter form submission
      const filterForm = availabilityFilter.closest('form');
      if (filterForm) {
        // Trigger change event to apply filter
        availabilityFilter.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  // Remove in-stock filter enforcement when leaving table view
  removeInStockFilter() {
    const wasUnchecked = sessionStorage.getItem('pre-table-view-stock-filter') === 'unchecked';

    if (wasUnchecked) {
      const availabilityFilter = document.querySelector('input[name="filter.v.availability"][value="1"]');

      if (availabilityFilter && availabilityFilter.checked) {
        // Uncheck the filter
        availabilityFilter.checked = false;

        // Trigger the filter form submission
        const filterForm = availabilityFilter.closest('form');
        if (filterForm) {
          availabilityFilter.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Clear the stored state
      sessionStorage.removeItem('pre-table-view-stock-filter');
    }
  }
}

// Enhanced view switcher with additional features
class EnhancedCollectionViewSwitcher extends CollectionViewSwitcher {
  constructor() {
    super();
    this.setupResponsiveHandling();
    this.setupKeyboardNavigation();
  }

  setupResponsiveHandling() {
    // Handle responsive view changes
    const mediaQuery = window.matchMedia('(max-width: 749px)');
    
    const handleResponsiveChange = (e) => {
      if (e.matches && this.currentView === 'table') {
        // On mobile, table view might be less usable, but we'll keep it
        // You could automatically switch to grid view here if desired
        // this.setView('grid');
      }
    };

    mediaQuery.addListener(handleResponsiveChange);
    handleResponsiveChange(mediaQuery); // Check initial state
  }

  setupKeyboardNavigation() {
    // Add keyboard navigation for accessibility
    this.viewToggleButtons.forEach((button, index) => {
      button.addEventListener('keydown', (e) => {
        let targetIndex = index;
        
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            targetIndex = index > 0 ? index - 1 : this.viewToggleButtons.length - 1;
            break;
          case 'ArrowRight':
            e.preventDefault();
            targetIndex = index < this.viewToggleButtons.length - 1 ? index + 1 : 0;
            break;
          case 'Home':
            e.preventDefault();
            targetIndex = 0;
            break;
          case 'End':
            e.preventDefault();
            targetIndex = this.viewToggleButtons.length - 1;
            break;
          default:
            return;
        }
        
        this.viewToggleButtons[targetIndex].focus();
      });
    });
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Use enhanced version with additional features
  window.collectionViewSwitcher = new EnhancedCollectionViewSwitcher();
});

// Handle dynamic content loading (for infinite scroll, AJAX, etc.)
document.addEventListener('collectionProductsLoaded', () => {
  if (window.collectionViewSwitcher) {
    window.collectionViewSwitcher.refresh();
  }
});

// Export for use in other scripts if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CollectionViewSwitcher, EnhancedCollectionViewSwitcher };
}
